import type { ReactNode } from 'react';
import { cx } from '../cx';
import { stepOf, heatSteps } from './Heatmap';
import { WIDE_ONLY_KEEP_SPACE, type ChartLayout } from './layout';
import { SEQ_FILL, TICK_TEXT } from './scale';

export type CalendarHeatmapProps = {
  /** Row names, in reading order (Monday first). */
  rows: ReactNode[];
  /** Column names (weeks, hours); null leaves a column unlabelled. */
  columns: Array<ReactNode | null>;
  /** values[row][column]; null for no data. */
  values: Array<Array<number | null>>;
  /** Five ascending boundaries for the six steps; defaults to round numbers from the data. */
  steps?: number[];
  /** `auto` (default) switches to the narrow form in a narrow container: compact cells and column labels on every other column. */
  layout?: ChartLayout;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  className?: string;
};

const COLUMN: Record<ChartLayout, string> = { auto: 'min-w-4 @md:min-w-5', narrow: 'min-w-4', wide: 'min-w-5' };
const CELL: Record<ChartLayout, string> = { auto: 'h-5 @max-md:h-4', narrow: 'h-4', wide: 'h-5' };
const ROW_LABEL: Record<ChartLayout, string> = { auto: 'min-w-10 @md:min-w-12', narrow: 'min-w-10', wide: 'min-w-12' };

/** Days by weeks (or hours by days) in the sequential ramp: when things happen. Values live in the table twin. */
export function CalendarHeatmap({
  rows,
  columns,
  values,
  steps,
  layout = 'auto',
  description,
  className,
}: CalendarHeatmapProps) {
  const bounds = steps ?? heatSteps(values.flat().filter((v): v is number => v != null));
  const last = columns.length - 1;
  return (
    <div className={cx('@container overflow-x-auto', className)}>
      <div
        role="img"
        aria-label={description}
        className="grid min-w-max gap-1"
        style={{ gridTemplateColumns: `auto repeat(${columns.length}, minmax(auto, 1fr))` }}
      >
        <span className={ROW_LABEL[layout]} />
        {columns.map((c, j) => (
          <span
            key={j}
            className={cx(
              'truncate text-center',
              COLUMN[layout],
              TICK_TEXT,
              j !== 0 &&
                j !== last &&
                (j % 2 === 1 || (last % 2 === 1 && j === last - 1)) &&
                WIDE_ONLY_KEEP_SPACE[layout],
            )}
          >
            {c}
          </span>
        ))}
        {rows.map((r, i) => (
          <div key={i} className="contents">
            <span className={cx('flex items-center', ROW_LABEL[layout], TICK_TEXT)}>{r}</span>
            {columns.map((_, j) => {
              const v = values[i]?.[j] ?? null;
              return (
                <span
                  key={j}
                  className={cx('rounded-xs', CELL[layout], v == null ? 'bg-jc-elevated' : SEQ_FILL[stepOf(v, bounds)])}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
